import axios from 'axios';
import get from 'lodash/get';
import Bugsnag from '@bugsnag/js';

import { account } from '@redux/account';
import store from './store';

export let domain;
let epgDomain;

switch(process.env.REACT_APP_ENV) {
  case 'production':
    domain = 'https://core.uniplayer.net';
    epgDomain = 'https://epg.uniplayer.net';
    break;
  case 'mirror':
    domain = 'https://core.uniplayer.app';
    epgDomain = 'https://epg.uniplayer.app';
    break;
  case 'staging':
    domain = 'https://core.uniplayer.dev';
    epgDomain = 'https://epg.uniplayer.net';
    break;
  default:
    domain = 'https://core.test';
    // domain = 'https://core.uniplayer.dev';
    epgDomain = 'https://epg.uniplayer.net';
    break;
}

export const api = axios.create({
  baseURL:  `${domain}/api/v1`,
  headers: {
    'Accept': 'application/json',
  },
});

export const apiV2 = axios.create({
  baseURL:  `${domain}/api/v2`,
  headers: {
    'Accept': 'application/json',
  },
});

export const apiEPG = axios.create({
  baseURL: `${epgDomain}/api`,
  headers: {
    'Accept': 'application/json',
  },
});

[api, apiV2, apiEPG].map((api) => {
  api.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const token = account.selectors.accessToken(state);
      const lang = account.selectors.language(state);

      config.headers.Authorization = token ? `Bearer ${token}` : 'Basic';
      config.headers.lang = lang;
      config.headers.FromSite = 1;
      return config;
    }
  );

  api.interceptors.response.use(
    (response) => {
      return Promise.resolve(get(response, 'config.unwrap') ? response.data : response.data.data || response.data || response);
    }, (error) => {
      // Handle case when there's no response (network error)
      if (!error.response) {
        const networkError = {
          status: 0,
          data: {
            message: 'Network Error: Could not connect to the server',
            errors: ['Network Error: Could not connect to the server']
          }
        };
        // Log the network error to Bugsnag if it's imported
        if (typeof Bugsnag !== 'undefined') {
          Bugsnag.notify(new Error('API Network Error'), function(event) {
            event.severity = 'error';
            event.addMetadata('request', {
              url: error.config?.url,
              method: error.config?.method
            });
          });
        }
        return Promise.reject(networkError);
      }

      const { response } = error;

      // Don't redirect to logout for 422 validation errors
      // 401 should only redirect when the user's session is invalid
      if (response.status === 401 && account.selectors.accessToken(store.getState())) {
        window.location.replace('/auth/logout');
        return Promise.reject({ status: response.status, data: response.data });
      }

      // Ensure response has data property with error information
      if (!response.data) {
        response.data = {
          message: `Error ${response.status}: ${response.statusText || 'Unknown error'}`,
          errors: [`Error ${response.status}: ${response.statusText || 'Unknown error'}`]
        };
      }

      if (typeof Bugsnag !== 'undefined') {
        const is422Error = response.status === 422;
        // Match both "/playlists/" and "playlists/" URLs - users may access deleted or non-existing playlists
        const isPlaylistData404 = response.status === 404 && /\/?playlists\//.test(error.config?.url) && error.config?.method === 'get';

        if (!is422Error && !isPlaylistData404) {
          Bugsnag.notify(new Error(`API Error: ${response.status}`), function(event) {
            event.severity = 'error';
            event.addMetadata('request', {
              url: error.config?.url,
              method: error.config?.method,
              status: response.status
            });
            event.addMetadata('response', {
              data: response.data
            });
          });
        }
      }

      return Promise.reject({ status: response.status, data: response.data });
    }
  );
  return api;
});
